import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "../shared";
import SearchableSelect from "../SearchableSelect";
import { ACCOUNTING_BASE } from "../accountingNav";
import { ReportFilterBar, ReportPrintShell, useFinancialYears, downloadReportCsv, formatCurrency, formatBalance, formatDateDMY } from "./reportShared";

/**
 * Phase 8 - General Ledger (items 6-8, 41).
 *
 * One Ledger's statement: Opening Balance as the first logical row (never a
 * fake ordinary transaction - item 41), every posted line after it with a
 * Dr/Cr-aware running balance (item 7), and a clickable Voucher No. that
 * opens Voucher View with Back navigation preserved (item 8).
 */
export default function GeneralLedgerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const financialYears = useFinancialYears();
  const [ledgers, setLedgers] = useState([]);
  const [ledgerId, setLedgerId] = useState(searchParams.get("ledgerId") || "");
  const [financialYearId, setFinancialYearId] = useState(searchParams.get("financialYearId") || "");
  const [fromDate, setFromDate] = useState(searchParams.get("fromDate") || "");
  const [toDate, setToDate] = useState(searchParams.get("toDate") || "");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/ledgers");
        setLedgers(Array.isArray(res.data) ? res.data : []);
      } catch { setLedgers([]); }
    })();
  }, []);

  const ledgerOptions = useMemo(() => ledgers.map((l) => ({ value: l.id, label: l.name, sublabel: l.ledgerGroup })), [ledgers]);
  const params = { ledgerId, financialYearId, fromDate, toDate };

  const load = async () => {
    if (!ledgerId) { setData(null); return; }
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/general-ledger", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load general ledger"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ledgerId, financialYearId, fromDate, toDate]);

  // Item 2 ("Provide: Date filter, Ledger filter, Search, Print, Excel
  // export"): a plain client-side text filter across the already-loaded
  // statement rows (Voucher No., Particulars, Party, Reference) - this
  // statement is for one ledger over one date range, a small enough list
  // that a server round-trip isn't needed, same "society-scale is small"
  // convention the Phase 4 master-list SearchBox already uses.
  const searchNeedle = search.trim().toLowerCase();
  const visibleRows = (data?.rows || []).filter((row) => {
    if (!searchNeedle) return true;
    return [row.voucherNumber, row.narration, row.partyName, row.referenceNo, row.voucherType].some((v) => (v || "").toLowerCase().includes(searchNeedle));
  });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">General Ledger</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Statement for one account, with opening balance, every posted transaction and a running balance.</p>
      </div>

      <div className="report-screen-only space-y-5">
        <ReportFilterBar
          financialYears={financialYears} financialYearId={financialYearId} onFinancialYearChange={setFinancialYearId}
          fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate}
          extra={
            <div className="w-64 space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Ledger</div>
              <SearchableSelect value={ledgerId} onChange={setLedgerId} options={ledgerOptions} placeholder="Select ledger" />
            </div>
          }
          search={search} onSearchChange={setSearch} searchPlaceholder="Voucher no., particulars, party, reference"
          onExport={ledgerId ? () => downloadReportCsv("/society/accounting/reports/general-ledger", params, `general-ledger-${ledgerId}.csv`) : undefined}
          onPrint={data ? () => window.print() : undefined}
        />

        {!ledgerId ? (
          <EmptyState>Select a ledger to view its General Ledger statement.</EmptyState>
        ) : loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : !data ? (
          <EmptyState>No data.</EmptyState>
        ) : (
          <SectionCard
            title={data.ledgerName}
            subtitle={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}
          >
            <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Opening</div><div className="mt-0.5 text-sm font-bold">{formatBalance(data.openingBalance, data.openingSide)}</div></div>
              <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Period Debit</div><div className="mt-0.5 text-sm font-bold">{formatCurrency(data.periodDebit)}</div></div>
              <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Period Credit</div><div className="mt-0.5 text-sm font-bold">{formatCurrency(data.periodCredit)}</div></div>
              <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Closing</div><div className="mt-0.5 text-sm font-bold">{formatBalance(data.closingBalance, data.closingSide)}</div></div>
            </div>
            <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead><TableHead>Voucher No.</TableHead><TableHead>Type</TableHead>
                    <TableHead>Particulars</TableHead><TableHead>Party</TableHead><TableHead>Reference</TableHead>
                    <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Running Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-[#F9FAFB]">
                    <TableCell>Opening</TableCell><TableCell colSpan={2}>Opening Balance</TableCell><TableCell colSpan={3}>—</TableCell>
                    <TableCell className="text-right">—</TableCell><TableCell className="text-right">—</TableCell>
                    <TableCell className="text-right font-semibold">{formatBalance(data.openingBalance, data.openingSide)}</TableCell>
                  </TableRow>
                  {visibleRows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{formatDateDMY(row.voucherDate)}</TableCell>
                      <TableCell>
                        {row.voucherId ? (
                          <button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${row.voucherId}`, { state: { back: `${ACCOUNTING_BASE}/reports/general-ledger?ledgerId=${ledgerId}` } })}>
                            {row.voucherNumber || "(draft)"}
                          </button>
                        ) : "-"}
                      </TableCell>
                      <TableCell>{row.voucherType}</TableCell>
                      <TableCell>{row.narration}</TableCell>
                      <TableCell>{row.partyName || "-"}</TableCell>
                      <TableCell>{row.referenceNo || "-"}</TableCell>
                      <TableCell className="text-right">{row.debit ? formatCurrency(row.debit) : "—"}</TableCell>
                      <TableCell className="text-right">{row.credit ? formatCurrency(row.credit) : "—"}</TableCell>
                      <TableCell className="text-right font-semibold">{formatBalance(row.runningBalance, row.runningSide)}</TableCell>
                    </TableRow>
                  ))}
                  {visibleRows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-sm text-[#6B7280]">{searchNeedle ? "No transactions match your search." : "No transactions in this range."}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </SectionCard>
        )}
      </div>

      {data && (
        <div className="report-print-only">
          <ReportPrintShell reportName={`General Ledger — ${data.ledgerName}`} rangeLabel={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
            <table className="w-full border-collapse text-[10px]">
              <thead>
                <tr className="border-b border-black">
                  <th className="py-1 text-left">Date</th><th className="py-1 text-left">Voucher</th><th className="py-1 text-left">Particulars</th>
                  <th className="py-1 text-right">Debit</th><th className="py-1 text-right">Credit</th><th className="py-1 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-300"><td className="py-0.5">Opening</td><td className="py-0.5">-</td><td className="py-0.5">Opening Balance</td><td /><td /><td className="py-0.5 text-right">{formatBalance(data.openingBalance, data.openingSide)}</td></tr>
                {visibleRows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-300">
                    <td className="py-0.5">{formatDateDMY(row.voucherDate)}</td>
                    <td className="py-0.5">{row.voucherNumber}</td>
                    <td className="py-0.5">{row.narration}</td>
                    <td className="py-0.5 text-right">{row.debit ? formatCurrency(row.debit) : ""}</td>
                    <td className="py-0.5 text-right">{row.credit ? formatCurrency(row.credit) : ""}</td>
                    <td className="py-0.5 text-right">{formatBalance(row.runningBalance, row.runningSide)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportPrintShell>
        </div>
      )}
    </div>
  );
}
